/**
 * ManageReferenceFieldsDialog
 *
 * Schema management for the per-user reference fields used by SSO and
 * external-system automations. Admins can:
 *   - Add a new field (key + label)
 *   - Rename either side of an existing field (the id is stable, so a
 *     rename preserves every user's value)
 *   - Delete a field globally (sweeps the per-user value map)
 *
 * Persists through the lookups slice. `onSchemaChanged` fires after every
 * mutation so the parent can prune staged values for removed fields.
 *
 * Scoped to account/super admins. The owner page gates the "Manage
 * fields" button on role; this dialog assumes it was opened by a
 * caller that already confirmed permission.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '../ui';
import { AddIcon, DeleteIcon } from '../icons';
import { useSelector } from 'react-redux';
import { UserReferenceField } from '../../apis/api-client';
import { useAppDispatch } from '../../stores';
import {
  createReferenceField,
  deleteReferenceField,
  selectAllReferenceFields,
  updateReferenceField,
  useEnsureReferenceFields,
} from '../../features/lookups/lookupsSlice';

interface ManageReferenceFieldsDialogProps {
  open: boolean;
  onClose: () => void;
  onSchemaChanged: () => void;
}

export default function ManageReferenceFieldsDialog({
  open,
  onClose,
  onSchemaChanged,
}: ManageReferenceFieldsDialogProps) {
  const dispatch = useAppDispatch();
  useEnsureReferenceFields();
  const reduxDefs = useSelector(selectAllReferenceFields);
  // Local editable mirror so the row inputs can edit optimistically and
  // commit on blur. Re-seeded whenever the authoritative list changes
  // (only on commit here — never mid-typing, since typing edits the mirror
  // without touching the slice).
  const [defs, setDefs] = useState<UserReferenceField[]>([]);
  // Two-field add row at the bottom. Cleared after each successful add
  // so admins can stamp in several fields in one sitting.
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDefs(reduxDefs);
  }, [reduxDefs]);

  useEffect(() => {
    if (open) {
      setNewKey('');
      setNewLabel('');
      setError(null);
    }
  }, [open]);

  const handleRename = async (
    id: number,
    patch: Partial<Pick<UserReferenceField, 'key' | 'label'>>,
  ) => {
    const def = defs.find((f) => f.id === id);
    if (!def) return;
    const request = {
      key: (patch.key ?? def.key ?? '').trim(),
      label: (patch.label ?? def.label ?? '').trim(),
      sequenceOrder: def.sequenceOrder,
    };
    if (!request.key || !request.label) {
      setDefs(reduxDefs); // revert blank edit
      return;
    }
    try {
      await dispatch(updateReferenceField({ id, request })).unwrap();
    } catch {
      setDefs(reduxDefs); // server rejected (e.g. key collision) — revert
    }
    onSchemaChanged();
  };

  const handleRemove = async (id: number) => {
    await dispatch(deleteReferenceField(id)).unwrap();
    onSchemaChanged();
  };

  const handleAdd = async () => {
    setError(null);
    try {
      await dispatch(
        createReferenceField({
          key: newKey.trim(),
          label: newLabel.trim(),
          sequenceOrder: reduxDefs.length,
        }),
      ).unwrap();
      setNewKey('');
      setNewLabel('');
      onSchemaChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not add field');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Manage reference fields
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Custom identifiers stored per user — used to link the LMS with
          SSO providers and HR / scheduling automations. Keys are the
          machine name your integrations read; labels are what admins see.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {defs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No reference fields yet. Add one below.
            </Typography>
          )}

          {defs.map((def) => (
            <Box
              key={def.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Label"
                value={def.label ?? ''}
                onChange={(e) =>
                  setDefs((cur) =>
                    cur.map((f) => (f.id === def.id ? { ...f, label: e.target.value } : f)),
                  )
                }
                onBlur={(e) => handleRename(def.id!, { label: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Key"
                value={def.key ?? ''}
                onChange={(e) =>
                  setDefs((cur) =>
                    cur.map((f) => (f.id === def.id ? { ...f, key: e.target.value } : f)),
                  )
                }
                onBlur={(e) => handleRename(def.id!, { key: e.target.value })}
                fullWidth
                helperText="e.g. hr_employee_id"
              />
              <Tooltip title="Remove field (clears it for every user)">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemove(def.id!)}
                  sx={{ alignSelf: 'center' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}

          {/* Add-row sits below the list so the action target (the +
              button) is the last thing the eye reaches — same shape as
              the rows above for a single visual rhythm. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            <TextField
              size="small"
              label="Label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. ADP Worker ID"
              fullWidth
            />
            <TextField
              size="small"
              label="Key"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="e.g. adp_worker_id"
              fullWidth
            />
            <Tooltip title="Add field">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAdd}
                  disabled={!newKey.trim() || !newLabel.trim()}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
